import { createAction } from '@reduxjs/toolkit';

import {
	PlayerReportRequestPayload,
	UpdateProfilePayload,
	UserProfile,
	UserStatistics,
} from '../types';
export type RegisterPayload = {
	email: string;
	password: string;
	username?: string;
	firstName?: string;
	lastName?: string;
	dateOfBirth?: string; // ISO string
	mobileNumber?: string;
	invitedBy?: string;
	nickname?: string;
	remarks?: string;
	ageConfirmed?: boolean;
};

export const updateUserVipStatus = createAction<{
	isVip: boolean;
	vipExpiredAt?: number;
}>('auth/updateUserVipStatus');

export const updateUserLevel = createAction<number>('auth/updateUserLevel');

export const loginRequest = createAction<{
	username: string;
	password: string;
	confirmed?: boolean;
}>('auth/loginRequest');

export type PendingLoginConfirmation =
	| {
			type: 'password';
			username: string;
			password: string;
	  }
	| {
			type: 'apple' | 'facebook' | 'google';
			idToken: string;
	  };
export type AuthFailurePayload = {
	message: string;
	error?: string;
	code?: string | number;
};

export const loginSuccess = createAction<{ token: string; user: any }>('auth/loginSuccess');
export const loginFailure = createAction<AuthFailurePayload>('auth/loginFailure');

export const logout = createAction('auth/logout');

export const registerRequest = createAction<RegisterPayload>('auth/register/request');
export const registerSuccess = createAction('auth/register/success');
export const registerFailure = createAction<AuthFailurePayload>('auth/register/failure');

export const checkAuthRequest = createAction('auth/checkAuthRequest');
export const checkAuthSuccess = createAction<{ token: string; user?: any }>(
	'auth/checkAuthSuccess',
);
export const checkAuthFailure = createAction('auth/checkAuthFailure');

export const rehydrateAuth = createAction<{
	token?: string;
	user?: UserProfile;
}>('auth/rehydrate');

export const profileRequest = createAction('auth/profileRequest');
export const profileSuccess = createAction<UserProfile>('auth/profileSuccess');
export const profileFailure = createAction<string>('auth/profileFailure');
export const updateBonusCooldown = createAction<{
	collectedAt: string;
	bonusHours?: string;
}>('auth/updateBonusCooldown');

export const userStatsRequest = createAction<string | undefined>('auth/userStatsRequest');
export const userStatsSuccess = createAction<UserStatistics>('auth/userStatsSuccess');
export const userStatsFailure = createAction<string>('auth/userStatsFailure');

export const updateProfileRequest = createAction<UpdateProfilePayload>(
	'auth/profile/update/request',
);

export const updateProfileSuccess = createAction('auth/profile/update/success');
export const updateProfileFailure = createAction<string>('auth/profile/update/failure');

export const reportUserRequest = createAction<PlayerReportRequestPayload>('auth/reportUserRequest');

export const reportUserSuccess = createAction('auth/reportUserSuccess');

export const reportUserFailure = createAction<string>('auth/reportUserFailure');

export const facebookLoginRequest = createAction(
	'auth/facebookLoginRequest',
	(payload?: { idToken?: string; confirmed?: boolean }) => ({ payload }),
);

export const facebookLoginSuccess = createAction<{ token: string; user: any }>(
	'auth/facebookLoginSuccess',
);

export const facebookLoginFailure = createAction<string>('auth/facebookLoginFailure');

export const appleLoginRequest = createAction(
	'auth/appleLoginRequest',
	(payload?: { idToken?: string; confirmed?: boolean }) => ({ payload }),
);

export const appleLoginFailure = createAction<string>('auth/appleLoginFailure');

export const googleLoginRequest = createAction(
	'auth/googleLoginRequest',
	(payload?: { idToken?: string; confirmed?: boolean }) => ({ payload }),
);

export const googleLoginFailure = createAction<string>('auth/googleLoginFailure');

export const setLoginPendingConfirmation = createAction<PendingLoginConfirmation>(
	'auth/setLoginPendingConfirmation',
);

export const clearLoginPendingConfirmation = createAction('auth/clearLoginPendingConfirmation');

/** Shown by BannedModal: a login refused with a T_AUTH_LOGIN_*_BANNED code, or a live session revoked by a ban. */
export type BanNotice = {
	code: string;
	message: string;
	type?: string;
	expiresAt?: string | null;
	reason?: string;
};

export const setBanNotice = createAction<BanNotice>('auth/setBanNotice');

export const clearBanNotice = createAction('auth/clearBanNotice');
